import FetchClients, { ClientOutput } from "../client";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import { recursiveCamelCaseCipherTypesafe } from "@src/global_functions/postgrestApi";
import { CamelCasedProperties } from "type-fest";
import moment from "moment";

const metrics = {
  useGetEnergyStarPropertyMetrics,
} as const;
export default metrics;

export const MINIMUM_ENERGY_STAR_SCORE_FOR_CERTIFICATION = 75;

export type MainLinkedEnergyStarPropertyMetrics = CamelCasedProperties<
  ClientOutput["site"]["EnergyStarMetricsResponse"]
>;

const getMainLinkedEnergyStarPropertyMetrics = async (siteId: number) => {
  const { data, error } = await FetchClients.site.GET(
    "/{site_id}/main_linked_es_property/metrics",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
    }
  );
  if (data) return recursiveCamelCaseCipherTypesafe(data);
  throw new Error(error);
};

function useGetEnergyStarPropertyMetrics(
  siteId?: number | null,
  // Now is only used for testing purposes -- since certification status is based on dates, now is mocked so that certification status remains constant in tests/stories
  now?: Date
) {
  return useReactQuery({
    queryKey: ["mainLinkedEnergyStarPropertyMetrics", siteId],
    queryFn: () => getMainLinkedEnergyStarPropertyMetrics(siteId!),
    select: (data) => {
      // Positive if increase needed, negative if score is already eligible for certification
      const diffFromMinimum = data.currentScore
        ? MINIMUM_ENERGY_STAR_SCORE_FOR_CERTIFICATION - data.currentScore
        : undefined;
      const eligibleForCertification =
        typeof diffFromMinimum === "number" ? diffFromMinimum <= 0 : undefined;

      const energyStarCertificationYearsWithScores = transformYearsWithScores(
        data.energyStarCertificationYearsWithScores
      );

      const latestScore =
        energyStarCertificationYearsWithScores?.[
          energyStarCertificationYearsWithScores.length - 1
        ];

      const certifiedOn =
        data?.energyStarCertificationLastApprovalDate != null
          ? moment(data.energyStarCertificationLastApprovalDate, "YYYY-MM-DD")
          : null;

      const isCertified =
        certifiedOn != null && moment(now).diff(certifiedOn, "months") <= 12;

      return {
        ...data,
        energyStarCertificationYearsWithScores,
        latestScore,
        eligibleForCertification,
        increaseNeededForCertification: eligibleForCertification
          ? undefined
          : diffFromMinimum,
        isCertified,
        certifiedOn,
        certifiedScore: latestScore?.energyStarScore,
      };
    },
    enabled: typeof siteId === "number",
  });
}

function transformYearsWithScores(yearsWithScores: string | null | undefined) {
  const years = yearsWithScores?.split(",");

  const scoresValue = years?.map((y) => {
    const parts = y.trim().split(" ");

    return {
      year: parts[0]!,
      energyStarScore: Number(parts[1]!.replace("(", "").replace(")", "")),
    };
  });

  return scoresValue;
}
